import React, { useEffect, useState, useCallback } from 'react';
import apiService from '../../services/api';
import '../../pages/Role/Role.css';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const searchIcon = apiService.getBlobAssetUrl('search.svg');
const editIcon = apiService.getBlobAssetUrl('edit_12.svg');
const deleteIcon = apiService.getBlobAssetUrl('delete_12.svg');
const addIcon = apiService.getBlobAssetUrl('Add_Icon.svg');
const downloadIcon = apiService.getBlobAssetUrl('download.svg');

const RoleTable = () => {
  const [roles, setRoles] = useState([]);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);


  const loadRoles = useCallback(async () => {
    try {
      const [rolesData] = await Promise.all([
        apiService.fetchData('roles'),
      ]);
      const rolesList = rolesData.results || [];
      const formattedData = rolesList.map(item => ({
        id: item.ID,
        role: item.Role || '',
        description: item.Description || '',
        modifiedBy: item.LastModifiedBy || '',
        modifiedOn: item.LastModifiedOn || '',
      }));

      setRoles(formattedData);
    } catch (error) {
      setSnackbarMessage('Failed to load data. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    return () => setRoles([]);
  }, [loadRoles]);

  const openSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const filteredRoles = roles.filter((role) => {
    const matchesSearch = Object.values(role)
      .join(' ')
      .toLowerCase()
      .includes(''.toLowerCase()); // Placeholder for searchTerm, which is removed
    return matchesSearch;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentRoles = filteredRoles.slice(indexOfFirst, indexOfLast);

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const roleId = roleToDelete.id;
      await apiService.deleteData('roles', { ID: roleId });
      setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
      setShowModal(false);
      openSnackbar('Role deleted successfully!', 'success');
    } catch (error) {
      openSnackbar('Failed to delete role. Please try again.', 'error');
      setShowModal(false);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
  };

  const closeEditModal = () => setEditingRole(null);

  const handleRoleUpdate = (updatedRole) => {
    const { role_id, updated_fields } = updatedRole;
    setRoles((prevRoles) =>
      prevRoles.map((r) =>
        r.id === role_id
          ? {
              ...r,
              role: updated_fields.Role || r.role,
              description: updated_fields.Description || r.description,
              selected_surveys: Array.isArray(updated_fields.selected_surveys)
                ? updated_fields.selected_surveys
                : r.selected_surveys,
              selected_projects: Array.isArray(updated_fields.selected_projects)
                ? updated_fields.selected_projects
                : r.selected_projects,
              modifiedBy: r.modifiedBy,
              modifiedOn: new Date().toISOString().split('T')[0],
            }
          : r
      )
    );
    closeEditModal();
    openSnackbar('Role updated successfully!', 'success');
    loadRoles();
  };

  const toggleDownloadMenu = () => setShowDownloadMenu(!showDownloadMenu);

  const exportCSV = () => {
    const headers = ['Role', 'Description', 'Last Modified By', 'Last Modified On'];
    const csvContent = [
      headers.join(','),
      ...currentRoles.map((r) =>
        [r.role, r.description, r.modifiedBy, r.modifiedOn]
          .map((value) => `"${value}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'roles.csv';
    a.click();
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.text('Role Table', 10, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Role', 'Description', 'Last Modified By', 'Last Modified On']],
        body: roles.map((r) => [r.role, r.description, r.modifiedBy, r.modifiedOn]),
      });
      doc.save('roles.pdf');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      openSnackbar('Failed to export PDF. Please try again.', 'error');
    }
  };

  return (
    <div className="role-table-container">
      <div className="role-page-header">
        <h2>Roles</h2>
      </div>

      <table className="role-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Description</th>
            <th>Last Modified By</th>
            <th>Last Modified On</th>
          </tr>
        </thead>
        <tbody>
          {currentRoles.length > 0 ? (
            currentRoles.map((role) => (
              <tr key={role.id}>
                <td>{role.role}</td>
                <td>{role.description}</td>
                <td>{role.modifiedBy}</td>
                <td>{role.modifiedOn}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                No results found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="custom-snackbar"
      >
        <Alert
          severity={snackbarSeverity}
          className={`custom-alert ${snackbarSeverity}`}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RoleTable;